/**
 * Redes sociales (1.0 admin Social): estado de la conexión con Meta, publicar ahora (vista previa o de
 * verdad), registro de publicaciones (`socialPosts`; en sandbox salen en «shadow») y acceso a la
 * configuración (`settings/socialPublisher`, sin secretos).
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import type { SocialPublishNowOutput, SocialTestConnectionOutput } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, muted, Notice, str, Table, td, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;
const DEFAULTS = {
  enabled: false,
  facebook_enabled: true,
  instagram_enabled: true,
  hours: [10, 15, 20],
  timezone: 'Europe/Madrid',
  tone: 'fun',
  mix: { listing: 60, seller: 15, category: 15, fact: 10 },
  blocked_categories: [
    'women_lingerie',
    'men_underwear',
    'girls_nightwear',
    'boys_nightwear',
    'women_swimwear',
    'health',
    'adult',
  ],
  min_price: 1,
  hashtags: '#nuvvu #secondhand #vintedalternative',
  ig_link_comment: true,
  languages: ['es', 'hu', 'it', 'fr', 'de'],
  page_id: '',
  ig_user_id: '',
};

export function SocialPage(): ReactElement {
  const [test, setTest] = useState<SocialTestConnectionOutput | null>(null);
  const [result, setResult] = useState<SocialPublishNowOutput | null>(null);
  const [posts, setPosts] = useState<Doc[] | null>(null);
  const [kind, setKind] = useState<'' | 'listing' | 'seller' | 'category' | 'fact'>('');
  const [productId, setProductId] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = (): void => {
    getDocs(query(collection(db, 'socialPosts'), orderBy('created_at', 'desc'), limit(50)))
      .then((s) => {
        setPosts(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {
        setPosts([]);
      });
  };
  useEffect(load, []);
  const run = (dry: boolean): void => {
    setBusy(true);
    setError(null);
    call(api.social.socialPublishNow, {
      ...(kind ? { kind } : {}),
      ...(productId.trim() ? { product_id: productId.trim() } : {}),
      ...(dry ? { dry_run: true } : {}),
    })
      .then((r) => {
        setResult(r);
        if (!dry) {
          setNotice(`Publicado: ${r.entry?.status ?? '?'}`);
          load();
        }
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };
  const seed = (): void => {
    call(api.admin.updateSettings, {
      doc: 'socialPublisher',
      data: DEFAULTS,
      reason: 'valores por defecto',
    })
      .then(() => {
        setNotice('settings/socialPublisher creado con los valores por defecto (desactivado).');
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  return (
    <>
      <h1 style={{ margin: 0 }}>Redes sociales</h1>
      <p style={muted}>
        Publica en la página de Facebook y el Instagram de nuvvu varias veces al día (horas en la
        configuración) una frase de Haiku en el idioma del ciclo. Los secretos (META_ACCESS_TOKEN,
        META_APP_SECRET) solo existen en producción; en sandbox las publicaciones quedan en
        «shadow». Los vendedores pueden excluirse desde Perfil → Cuenta.
      </p>
      <Notice text={notice} />
      <Notice text={error} error />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          style={btn(false)}
          onClick={() => {
            call(api.social.socialTestConnection, {})
              .then(setTest)
              .catch((e: unknown) => {
                setError(errorText(e));
              });
          }}
        >
          Probar conexión con Meta
        </button>
        <Link to="/ajustes/socialPublisher" style={btn(false)}>
          Configuración
        </Link>
        <button type="button" style={btn(false)} onClick={seed}>
          Crear configuración por defecto
        </button>
      </div>
      {test && (
        <div style={card}>
          <div>configurado: {test.configured ? 'sí' : 'no'}</div>
          {test.page && (
            <div>
              Página: {test.page.name} (#{test.page.id})
            </div>
          )}
          {test.instagram && (
            <div>
              Instagram: @{test.instagram.username} (#{test.instagram.id})
            </div>
          )}
          {test.errors.map((e) => (
            <div key={e} style={{ color: '#991B1B' }}>
              {e}
            </div>
          ))}
        </div>
      )}
      <div style={{ ...card, display: 'grid', gap: 8, maxWidth: 640 }}>
        <strong>Publicar ahora</strong>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            style={{ ...field, width: 180 }}
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as typeof kind);
            }}
          >
            <option value="">tipo: según el reparto</option>
            <option value="listing">anuncio</option>
            <option value="seller">la tienda de hoy</option>
            <option value="category">categoría</option>
            <option value="fact">dato del marketplace</option>
          </select>
          <input
            style={{ ...field, width: 240 }}
            placeholder="id de producto (opcional)"
            value={productId}
            onChange={(e) => {
              setProductId(e.target.value);
            }}
          />
          <button
            type="button"
            style={btn(false)}
            disabled={busy}
            onClick={() => {
              run(true);
            }}
          >
            Vista previa
          </button>
          <button
            type="button"
            style={btn(true)}
            disabled={busy}
            onClick={() => {
              if (window.confirm('¿Publicar ahora en Facebook/Instagram?')) run(false);
            }}
          >
            Publicar
          </button>
        </div>
        {result && (
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 10, fontSize: 13 }}>
            {result.preview.image && (
              <img
                src={result.preview.image}
                alt=""
                style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
              />
            )}
            <div style={{ whiteSpace: 'pre-wrap' }}>
              <div style={muted}>
                {result.preview.kind} · {result.preview.lang} · {result.preview.caption_by}
              </div>
              {result.preview.caption}
              <div style={muted}>{result.preview.link}</div>
              {result.entry && (
                <div style={muted}>
                  estado: {result.entry.status} {result.entry.errors.join(' · ')}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <h2 style={{ margin: 0, fontSize: 16 }}>Publicaciones</h2>
      {posts && (
        <Table head={['Fecha', 'Origen', 'Tipo', 'Idioma', 'Estado', 'Texto', 'Errores']}>
          {posts.map((p) => (
            <tr key={str(p.id)}>
              <td style={td}>{when(p.created_at)}</td>
              <td style={td}>{str(p.source)}</td>
              <td style={td}>{str(p.kind)}</td>
              <td style={td}>{str(p.lang)}</td>
              <td style={td}>{str(p.status)}</td>
              <td style={{ ...td, maxWidth: 360, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                {str(p.caption).slice(0, 220)}
              </td>
              <td style={{ ...td, fontSize: 11, color: '#991B1B' }}>
                {((p.errors as string[] | undefined) ?? []).join(' · ')}
              </td>
            </tr>
          ))}
          {posts.length === 0 && (
            <tr>
              <td style={td} colSpan={7}>
                Ninguna.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}
